import * as React from 'react'
import { useMemo } from 'react'
import { Theme, useTheme } from '@mui/material/styles'

import { Chevron } from '@sales-center/components'
import { ClosedDecoration } from './ClosedDecoration'
import { LEAD_STATUSES } from '../Leads/LeadCard/typings'
import { OPPORTUNITY_STATUSES } from '../Opportunities/OpportunityCard/typings'
import { RequirementsIndicator } from './atoms/RequirementsIndicator'

import Box from '@mui/material/Box'
import type {
  CardClosingRequirements,
  ENTITY_TYPES,
  IServiceDTO,
} from '@sales-center/common'

const getStyles = (theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    fontSize: '14px',
    lineHeight: '40px',
    cursor: 'pointer',
    transition: 'all 0.25s',
    '&:hover': {
      backgroundColor: '#f7f7f7',
    },
  },

  closedWon: { backgroundColor: theme.palette.common.white },
  closedLost: { backgroundColor: '#ffbcbc42' },
  drawerLabel: { transition: 'all 0.25s ease-in-out' },
  drawerOpen: { opacity: 0 },

  footerClosed: {
    borderTop: '1px solid #dddddd',
  },

  footerOpen: {
    boxShadow: '1px 4px 4px 0px #dddddd',
  },

  oppClosed: {
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
  },
})

interface Props {
  children?: React.ReactNode
  handleOpenDrawer: () => void
  isOpen: boolean
  entityId: number
  entityType: ENTITY_TYPES
  entityDetails?: {
    closedDate: Date | null
    reason?: string
    servicesCount: number
    services?: IServiceDTO[]
    status: LEAD_STATUSES | OPPORTUNITY_STATUSES
    closingRequirements: CardClosingRequirements
  }
  title?: string
  schedulingVisible: boolean
  closedAction?: React.ReactNode
}

export function EntityCardFooter(props: Props) {
  const {
    children,
    handleOpenDrawer,
    isOpen,
    entityDetails,
    title,
    entityId,
    entityType,
    schedulingVisible,
    closedAction,
  } = props

  const isDisqualifiedLead = useMemo(() => {
    return entityDetails && entityDetails.status == LEAD_STATUSES.Disqualified
  }, [entityDetails])

  const isClosedOpportunity = useMemo(() => {
    return entityDetails && entityDetails.status != OPPORTUNITY_STATUSES.Open
  }, [entityDetails])

  const styles = getStyles(useTheme())
  return (
    <Box
      data-test-id={`cardFooter-${entityId}`}
      sx={[
        styles.root,
        entityDetails &&
        (entityDetails.status === 'lost' ||
          entityDetails.status === 'disqualified')
          ? styles.closedLost
          : {},
        entityDetails && entityDetails.status === 'won' ? styles.closedWon : {},
        isOpen ? styles.footerOpen : styles.footerClosed,
        closedAction ? { alignItems: 'center', py: '5px' } : {},
      ]}
      px={2}
      onClick={handleOpenDrawer}
    >
      {children && children}
      {!children && (
        <>
          {!!entityDetails &&
            entityDetails.status !== LEAD_STATUSES.Open &&
            entityDetails.status !== OPPORTUNITY_STATUSES.Open && (
              <Box sx={{ minWidth: 0 }}>
                <ClosedDecoration
                  date={entityDetails.closedDate}
                  reason={entityDetails.reason}
                  status={entityDetails.status}
                />
              </Box>
            )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              ...(!!entityDetails &&
                entityDetails.status !== 'open' &&
                styles.oppClosed),
            }}
          >
            {closedAction}
            <Box
              sx={{
                ...styles.drawerLabel,
                ...(isOpen && styles.drawerOpen),
                whiteSpace: 'nowrap',
                ...(isOpen && !!closedAction && { display: 'none' }),
              }}
            >
              {entityDetails && !isDisqualifiedLead && !isClosedOpportunity ? (
                <RequirementsIndicator
                  entityId={entityId}
                  entityType={entityType}
                  schedulingVisible={schedulingVisible}
                  services={entityDetails.services ?? []}
                  servicesCount={entityDetails.servicesCount}
                  requirements={entityDetails.closingRequirements}
                />
              ) : (
                <>{title || 'Requirements to Close / Win'}</>
              )}
              <></>
            </Box>
          </Box>
        </>
      )}
      <Chevron open={isOpen} dataTestId={`cardFooterChevron-${entityId}`} />
    </Box>
  )
}
